import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import GroupScoreEntry from '../components/rounds/GroupScoreEntry';
import GroupSelector from '../components/rounds/GroupSelector';
import Leaderboard from '../components/rounds/Leaderboard';
import { roundsAPI, scoresAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { ROUTES } from '../utils/constants';
import { successHaptic, errorHaptic, heavyHaptic, mediumHaptic } from '../utils/haptics';

// Poll the leaderboard on an interval (same pattern as FriDave) so it updates
// inline without a full page refresh or reliance on the live socket.
const POLL_INTERVAL = 15000;

export default function ActiveRound() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { socket, connected, joinRound, leaveRound, addNotification } = useWebSocket();

  const [round, setRound] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Determine which view to show based on route state
  const activeTab = location.state?.activeTab || 'leaderboard';

  // Where to send the admin after completing or deleting this round. Guest
  // events must land back in their own shell, not on the Society's history.
  const isClubhouse = location.pathname.startsWith('/clubhouse');
  const exitPath = isClubhouse ? ROUTES.CLUBHOUSE_HOME : ROUTES.HISTORY;

  useEffect(() => {
    fetchRoundData();

    // Load saved group selection from localStorage
    const savedGroup = localStorage.getItem(`round_${id}_group`);
    if (savedGroup) {
      setSelectedGroup(parseInt(savedGroup));
    }
  }, [id, refreshTrigger]);

  // Inline leaderboard polling (FriDave pattern): while the round is live, quietly
  // refetch just the leaderboard and update it in place — no loading spinner, no
  // navigation. This keeps mobile in sync even if the live socket drops.
  useEffect(() => {
    if (!round || !(round.status === 'in_progress' || round.status === 'active')) return;
    const interval = setInterval(async () => {
      const lb = await roundsAPI.getLeaderboard(id).catch(() => null);
      if (lb) setLeaderboard(Array.isArray(lb) ? lb : []);
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [round, id]);

  // WebSocket setup - join round and listen for updates
  useEffect(() => {
    if (!socket || !connected || !id) return;

    // Join the round room
    joinRound(id);

    // Listen for score updates
    socket.on('score:updated', (data) => {
      console.log('[WebSocket] Score updated:', data);

      // Show notification
      // addNotification({
      //   type: 'score',
      //   title: 'Score Updated!',
      //   message: `${data.playerName} scored on hole ${data.holeNumber}`,
      //   subtitle: `${data.points} points`
      // });

      // Don't refresh here - GroupScoreEntry handles its own local state updates
      // and the leaderboard:updated event handles updating the leaderboard display
    });

    // Leaderboard updates come from the 15s inline poll only (same as FriDave).
    // We deliberately do NOT setLeaderboard from the socket: a batch score submit
    // fires this event once per player, so driving state off it caused a burst of
    // re-renders that flashed the page on every save. Polling keeps it in sync
    // without the flash.
    socket.on('leaderboard:updated', () => {});

    // Listen for round completion
    socket.on('round:completed', (data) => {
      console.log('[WebSocket] Round completed:', data);

      // addNotification({
      //   type: 'complete',
      //   title: 'Round Complete!',
      //   message: `Winner: ${data.winner}`,
      //   subtitle: 'Congratulations!'
      // });

      // Redirect to history after a delay
      setTimeout(() => {
        navigate(exitPath);
      }, 3000);
    });

    // Cleanup on unmount or when round changes
    return () => {
      socket.off('score:updated');
      socket.off('leaderboard:updated');
      socket.off('round:completed');
      leaveRound(id);
    };
  }, [socket, connected, id, addNotification, navigate]);

  const fetchRoundData = async () => {
    try {
      setLoading(true);
      const [roundData, leaderboardData] = await Promise.all([
        roundsAPI.getById(id),
        roundsAPI.getLeaderboard(id)
      ]);
      setRound(roundData);
      setLeaderboard(leaderboardData);
    } catch (err) {
      setError('Failed to load round: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSelect = (groupNum) => {
    setSelectedGroup(groupNum);
    // Save to localStorage for persistence
    localStorage.setItem(`round_${id}_group`, groupNum.toString());
  };

  const handleChangeGroup = () => {
    setSelectedGroup(null);
    localStorage.removeItem(`round_${id}_group`);
  };

  const handleScoreSubmitted = async () => {
    // Update the leaderboard inline after a score — do NOT bump refreshTrigger,
    // which re-runs fetchRoundData and flips loading=true, blanking the whole
    // page to a spinner on every score. A score doesn't change round metadata,
    // so only the leaderboard needs refreshing (silently, no loading state).
    const lb = await roundsAPI.getLeaderboard(id).catch(() => null);
    if (lb) setLeaderboard(Array.isArray(lb) ? lb : []);
  };

  const handleStartRound = async () => {
    mediumHaptic();

    if (!window.confirm('Start this round? Players will be able to enter scores.')) {
      return;
    }

    try {
      await roundsAPI.start(id);
      successHaptic();
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      errorHaptic();
      setError('Failed to start round: ' + err.message);
    }
  };

  const handleCompleteRound = async () => {
    mediumHaptic();

    if (!window.confirm('Are you sure you want to complete this round? This cannot be undone.')) {
      return;
    }

    try {
      await roundsAPI.complete(id);
      successHaptic();
      navigate(exitPath);
    } catch (err) {
      errorHaptic();
      console.error('Complete round error:', err);
      console.error('Error response:', err.response);

      // Show the detailed error message from the backend
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);

      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleResolveTie = async (winnerId, winnerName) => {
    if (!window.confirm(`Confirm playoff winner: ${winnerName}?\n\nThis will set their final position and cannot be undone.`)) return;
    try {
      mediumHaptic();
      const result = await roundsAPI.resolveTie(id, winnerId);
      successHaptic();
      setLeaderboard(result.leaderboard);
    } catch (err) {
      errorHaptic();
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleDeleteRound = async () => {
    heavyHaptic(); // Haptic for destructive action warning

    if (!window.confirm('⚠️ DELETE ROUND?\n\nThis will permanently delete:\n- This round\n- All player scores\n- All round data\n\nThis will NOT write to history.\n\nAre you sure?')) {
      return;
    }

    try {
      await roundsAPI.delete(id);

      // Success feedback
      successHaptic();
      setError(''); // Clear any existing errors

      // Show success message using the error state (green styling added below)
      const successMessage = '✓ Round deleted successfully';

      // Create a temporary success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 font-semibold';
      notification.textContent = successMessage;
      document.body.appendChild(notification);

      // Navigate after showing message
      setTimeout(() => {
        document.body.removeChild(notification);
        navigate(exitPath);
      }, 1500);
    } catch (err) {
      errorHaptic();
      console.error('Delete round error:', err);
      const errorMessage = err.response?.data?.message || err.message;
      setError('Failed to delete round: ' + errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'not_started': { text: 'Not Started', color: 'bg-gray-100 text-gray-800' },
      'in_progress': { text: 'In Progress', color: 'bg-green-100 text-green-800' },
      'active': { text: 'In Progress', color: 'bg-green-100 text-green-800' },
      'completed': { text: 'Completed', color: 'bg-blue-100 text-blue-800' }
    };
    const badge = badges[status] || badges['not_started'];
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const canEnterScores = round && (round.status === 'in_progress' || round.status === 'active');
  const canStartRound = round && round.status === 'not_started';
  const canCompleteRound = round && (round.status === 'in_progress' || round.status === 'active');

  // Tie detection on completed TML championship rounds
  const isTmlChampionship = round && round.event_type === 'tml' && round.round_type === 'championship';
  const isCompleted = round && round.status === 'completed';

  // Championship tie: 2+ players share position 1 with same points + strokes
  const tiedForFirst = isCompleted && isTmlChampionship
    ? leaderboard.filter((p, i, arr) =>
        i === 0 || (p.total_points === arr[0].total_points && p.total_strokes === arr[0].total_strokes)
      )
    : [];
  const hasChampionshipTie = tiedForFirst.length >= 2;

  // Bowl tie: 2+ bowl players share the same total_points + total_strokes at the top of the bowl
  const bowlPlayers = isCompleted && isTmlChampionship
    ? leaderboard.filter(p => p.is_bowl)
    : [];
  const tiedForBowl = bowlPlayers.length >= 2 && bowlPlayers[0].total_points === bowlPlayers[1].total_points && bowlPlayers[0].total_strokes === bowlPlayers[1].total_strokes
    ? bowlPlayers.filter(p => p.total_points === bowlPlayers[0].total_points && p.total_strokes === bowlPlayers[0].total_strokes)
    : [];
  const hasBowlTie = tiedForBowl.length >= 2;

  // Check if all players have completed all holes (handle both string and number types)
  const allPlayersComplete = leaderboard.every(player => Number(player.holes_completed) === 18);
  const incompletePlayers = leaderboard.filter(player => Number(player.holes_completed) < 18);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!round) {
    return (
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Round Not Found</h1>
        <Card>
          <p className="text-xl text-gray-600">The round you are looking for does not exist.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="pb-20">
      {/* Round Header */}
      <div className="mb-4 px-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-0.5">
              {round.event_name || 'Round'}
            </h1>
            <p className="text-sm text-gray-500">{round.course_name}</p>
          </div>
          <div>
            {getStatusBadge(round.status)}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-600 text-white px-4 py-3 mb-4 shadow-lg mx-4">
          <div className="flex items-start gap-2">
            <span className="text-xl">⚠️</span>
            <div className="flex-1">
              <p className="font-semibold mb-1">Error</p>
              <p className="text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-white hover:text-gray-200 text-xl"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div>
        {activeTab === 'leaderboard' && (
          <div>
            <Leaderboard leaderboard={leaderboard} round={round} />

            {/* Tie resolution panels — shown after playoff is played on course */}
            {isAuthenticated && isCompleted && isTmlChampionship && (hasChampionshipTie || hasBowlTie) && (
              <div className="px-4 mt-4 space-y-3">
                {hasChampionshipTie && (
                  <div className="border-2 border-yellow-400 rounded-lg p-4 bg-yellow-50">
                    <p className="text-sm font-bold text-yellow-900 mb-1">Championship Tie — Playoff Result</p>
                    <p className="text-xs text-yellow-700 mb-3">Select the playoff winner to resolve the tie.</p>
                    <div className="space-y-2">
                      {tiedForFirst.map(p => (
                        <button
                          key={p.player_id}
                          onClick={() => handleResolveTie(p.player_id, p.player_name)}
                          className="w-full py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm"
                        >
                          {p.player_name} wins the playoff
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {hasBowlTie && (
                  <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                    <p className="text-sm font-bold text-gray-800 mb-1">Bowl Tie — Playoff Result</p>
                    <p className="text-xs text-gray-600 mb-3">Select the bowl playoff winner.</p>
                    <div className="space-y-2">
                      {tiedForBowl.map(p => (
                        <button
                          key={p.player_id}
                          onClick={() => handleResolveTie(p.player_id, p.player_name)}
                          className="w-full py-2.5 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors text-sm"
                        >
                          {p.player_name} wins the bowl playoff
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Admin Actions — TML only; FriDave and The Clubhouse manage rounds via their own Manage Events page */}
            {isAuthenticated && round.event_type !== 'fridave' && round.event_type !== 'open' && (
              <div className="px-4 mt-4 space-y-3">
                {canStartRound && (
                  <button
                    onClick={handleStartRound}
                    className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Start Round
                  </button>
                )}
                {canCompleteRound && (
                  <div>
                    {!allPlayersComplete && leaderboard.some(p => Number(p.holes_completed) > 0) && (
                      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-3">
                        <p className="text-sm text-yellow-800 font-semibold mb-2">⚠️ Not all players have completed 18 holes</p>
                        <p className="text-xs text-yellow-700">Incomplete players ({incompletePlayers.length}):</p>
                        <ul className="text-xs text-yellow-700 mt-1 list-disc list-inside">
                          {incompletePlayers.slice(0, 5).map(p => (
                            <li key={p.player_id}>{p.player_name} ({p.holes_completed}/18)</li>
                          ))}
                          {incompletePlayers.length > 5 && <li>...and {incompletePlayers.length - 5} more</li>}
                        </ul>
                      </div>
                    )}
                    <button
                      onClick={handleCompleteRound}
                      className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Complete Round
                    </button>
                  </div>
                )}
                <button
                  onClick={handleDeleteRound}
                  className="w-full py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors border-2 border-red-700"
                >
                  🗑️ Delete Round (Testing)
                </button>
              </div>
            )}

          </div>
        )}

        {activeTab === 'enter-scores' && (
          <div>
            {!canEnterScores ? (
              <Card>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 font-semibold text-sm">
                    {round.status === 'not_started' && 'Round has not started yet. Admin must start the round before scores can be entered.'}
                    {round.status === 'completed' && 'This round has been completed. Scores can no longer be entered.'}
                  </p>
                </div>
              </Card>
            ) : !selectedGroup ? (
              <GroupSelector round={round} onGroupSelect={handleGroupSelect} />
            ) : (
              <div>
                <div className="mb-4 px-4">
                  <button
                    onClick={handleChangeGroup}
                    className="w-full min-h-touch-lg px-4 py-3 bg-white border-2 border-gray-300 hover:border-primary hover:bg-gray-50 text-gray-900 font-semibold text-base transition-all flex items-center justify-center gap-2"
                  >
                    <span>←</span>
                    <span>Change Group (Currently: Group {selectedGroup})</span>
                  </button>
                </div>
                <GroupScoreEntry
                  round={round}
                  groupNumber={selectedGroup}
                  onScoreSubmitted={handleScoreSubmitted}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
